import * as R from 'ramda';
import { put, call, delay, select, takeLatest } from 'redux-saga/effects';
// components
import { closeModal } from '../../../components/modal/actions';
import { openLoader, closeLoader } from '../../../components/loader/actions';
// features
import { makeSelectCurrentBranchGuid } from '../../branch/selectors';
import { getAllAvailableRefTypesByScopeRequest } from '../../reference/actions';
import { setInitialRouteGuid, setEditTemplateRoute } from '../../dispatch-planner-v2/actions';
import {
  checkReportFunction,
  transformSearchCriteriaBeforeReportPost,
} from '../../../components/edit-report/helpers';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// report-common
import { generateDefaultReport } from '../../../report-common';
// sagas
import { visitPageSaga } from '../../../sagas';
// utilities
import routesMap from '../../../utilities/routes';
import { sendRequest } from '../../../utilities/http';
import endpointsMap from '../../../utilities/endpoints';
// feature template-report
import * as A from './actions';
import { getRequiredFieldsByReportType, getReferenceScopeByReportType } from './settings';
import {
  makeSelectListType,
  makeSelectReportType,
  makeSelectUsedReport,
  makeSelectPagination,
  makeSelectTitleSortValues,
  makeSelectGlobalFilterValue,
  makeSelectTableTitleFilters,
} from './selectors';
//////////////////////////////////////////////////

const templateEndpoints = {
  [GC.ITEM_REPORT]: {
    template: endpointsMap.item,
    listUp: endpointsMap.listItemUp,
    listDown: endpointsMap.listItemDown,
    reference: endpointsMap.itemReference,
    listUpExport: endpointsMap.itemUpExportReport,
    activate: endpointsMap.getActivateItemEndpoint,
    listDownExport: endpointsMap.itemDownExportReport,
    deactivate: endpointsMap.getDeactivateItemEndpoint,
  },
  [GC.LOCATION_TEMPLATE_REPORT]: {
    template: endpointsMap.location,
    listUp: endpointsMap.listLocationsUp,
    listDown: endpointsMap.listLocationsDown,
    reference: endpointsMap.locationReference,
    listUpExport: endpointsMap.locationUpExportReport,
    activate: endpointsMap.getActivateLocationEndpoint,
    listDownExport: endpointsMap.locationDownExportReport,
    deactivate: endpointsMap.getDeactivateLocationEndpoint,
  },
  [GC.CONTACT_BOOK_REPORT]: {
    template: endpointsMap.contactBook,
    listUp: endpointsMap.contactBookListUp,
    listDown: endpointsMap.contactBookListDown,
    listUpExport: endpointsMap.contactBookListUpExport,
    listDownExport: endpointsMap.contactBookListDownExport,
  },
  [GC.CONTAINER_TEMPLATE_REPORT]: {
    template: endpointsMap.containerTemplate,
    listUp: endpointsMap.listContainerTemplateUp,
    listDown: endpointsMap.listContainerTemplateDown,
    listUpExport: endpointsMap.containerTemplateUpExportReport,
    listDownExport: endpointsMap.containerTemplateDownExportReport,
  },
  [GC.COMPENSATION_TEMPLATE_REPORT]: {
    template: endpointsMap.compensationTemplate,
    listUp: endpointsMap.compensationTemplateListUp,
    listDown: endpointsMap.compensationTemplateListDown,
    massDelete: endpointsMap.compensationTemplateMassDelete,
    listUpExport: endpointsMap.compensationTemplateListUpExport,
    listDownExport: endpointsMap.compensationTemplateListDownExport,
  },
  [GC.SHARED_ACCESSORIAL_REPORT]: {
    template: endpointsMap.sharedAccessorial,
    list: endpointsMap.sharedAccessorialList,
  },
  [GC.ROUTE_TEMPLATE_REPORT]: {
    template: endpointsMap.routeTemplate,
    list: endpointsMap.routeTemplateList,
    activate: endpointsMap.getActivateRouteTemplateEndpoint,
    deactivate: endpointsMap.getDeactivateRouteTemplateEndpoint,
  },
  [GC.CLO_TEMPLATE_REPORT]: {
    template: endpointsMap.cloTemplate,
    list: endpointsMap.cloTemplateList,
  },
  [GC.EQUIPMENT_SERVICE_TEMPLATE_REPORT]: {
    template: endpointsMap.fleetEquipmentServiceTemplate,
    remove: endpointsMap.fleetEquipmentServiceTemplateByGuid,
    listUp: endpointsMap.fleetEquipmentServiceTemplateListUp,
    listDown: endpointsMap.fleetEquipmentServiceTemplateListDown,
    listUpExport: endpointsMap.fleetEquipmentServiceTemplateListUpExport,
    listDownExport: endpointsMap.fleetEquipmentServiceTemplateListDownExport,
  },
};

function* getItemListRequest(action) {
  try {
    const reportParams = yield select(makeSelectUsedReport());

    if (G.isNilOrEmpty(R.prop('fields', reportParams))) return;

    yield put(A.setListLoading(true));

    const listType = yield select(makeSelectListType());
    const pagination = yield select(makeSelectPagination());
    const reportType = yield select(makeSelectReportType());
    const titleOrderFields = yield select(makeSelectTitleSortValues());
    const titleFilterParams = yield select(makeSelectTableTitleFilters());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());
    const globalFilterValue = yield select(makeSelectGlobalFilterValue());

    const fields = G.getOrElse(reportParams, 'fields', []);

    const orderFields = G.ifElse(
      G.isNotEmpty(titleOrderFields),
      R.values(titleOrderFields),
      G.getOrElse(reportParams, 'orderFields', []),
    );

    const searchCriteria = G.getSearchCriteriaWithTitleFilters(reportParams, titleFilterParams);

    const guids = R.pathOr(null, ['payload', 'guids'], action);

    const reqBody = {
      ...pagination,
      guids,
      fields,
      orderFields,
      globalFilterValue,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      searchCriteria: transformSearchCriteriaBeforeReportPost(searchCriteria, reportType),
    };

    if (G.isNotEmpty(reqBody.fields)) {
      const requiredFields = getRequiredFieldsByReportType(reportType);

      reqBody.fields = G.addRequiredFields(reqBody.fields, requiredFields);

      if (R.equals(reportType, GC.SHARED_ACCESSORIAL_REPORT)) {
        reqBody.fields = R.append(
          {
            sequence: 100,
            freezed: false,
            reference: false,
            collection: true,
            name: GC.FIELD_ACCESSORIAL_FUEL_INDEX_VARIABLES,
          },
          reqBody.fields,
        );
      }
    }

    const options = {
      data: reqBody,
    };

    const endpoint = R.path([reportType, listType], templateEndpoints);

    const res = yield call(sendRequest, 'post', endpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.setListLoading(false));
      yield put(A.getItemListSuccess({ data, guids }));
    } else {
      yield put(A.setListLoading(false));

      yield call(G.handleFailResponse, res, 'getItemListRequest fail');
    }
  } catch (error) {
    yield put(A.setListLoading(false));

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'getItemListRequest exception');
  }
}

function* getAvailableReportsRequest({ payload }) {
  try {
    const { setUsedReport } = payload;

    const reportType = yield select(makeSelectReportType());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());
    const params = {
      reportType,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
    };

    const res = yield call(sendRequest, 'get', endpointsMap.listReports, { params });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const reports = G.getReportsSortedBySeqFreez(data);
      const checkedReports = checkReportFunction(reports);
      yield put(A.setReports(checkedReports));

      if (R.not(R.prop('length', reports))) {
        return yield put(A.setUsedReport(generateDefaultReport(reportType)));
      }

      if (G.isTrue(setUsedReport)) {
        const defaultReport = G.findDefaultReport(checkedReports);
        const usedReport = R.or(defaultReport, generateDefaultReport(reportType));

        yield put(A.setUsedReport(usedReport));
      }
    } else {
      yield call(G.handleFailResponse, res, 'getAvailableReportsRequest fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'getAvailableReportsRequest exception');
  }
}

function* createReportRequest({ payload }) {
  try {
    yield put(openLoader());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());
    const options = {
      data: R.assoc(GC.FIELD_BRANCH_GUID, currentBranchGuid, payload),
    };

    const res = yield call(sendRequest, 'post', endpointsMap.report, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.setUsedReport(G.getReportSortedBySeqFreez(R.head(checkReportFunction(R.of(Array, data))))));
      yield put(A.getAvailableReportsRequest({ setUsedReport: false }));
      yield put(A.getItemListRequest());
    } else {
      yield call(G.handleFailResponse, res, 'createReportRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'createReportRequest exception');
  }
}

function* updateReportRequest({ payload }) {
  try {
    yield put(openLoader());

    const res = yield call(sendRequest, 'put', endpointsMap.report, { data: payload });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.setUsedReport(G.getReportSortedBySeqFreez(R.head(checkReportFunction(R.of(Array, data))))));
      yield put(A.getAvailableReportsRequest({ setUsedReport: false }));
      yield put(A.getItemListRequest());
    } else {
      yield call(G.handleFailResponse, res, 'updateReportRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'updateReportRequest exception');
  }
}

function* changeDefaultReportRequest({ payload }) {
  try {
    yield put(openLoader());

    const res = yield call(sendRequest, 'put', endpointsMap.changeDefaultReport, { data: payload });

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(getAvailableReportsRequest, { payload: { setUsedReport: true } });

      yield put(A.resetListAndPagination());
      yield put(A.getItemListRequest());
    } else {
      yield call(G.handleFailResponse, res, 'handleChangeDefaultReportSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleChangeDefaultReportSaga exception');
  }
}

function* exportReportDataRequest({ payload }) {
  try {
    const {
      name,
      fields,
      fileType,
      orderFields,
      searchCriteria,
    } = payload;

    yield put(openLoader({ showDimmer: true }));

    const listType = yield select(makeSelectListType());
    const reportType = yield select(makeSelectReportType());
    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const reqBody = {
      fields,
      orderFields,
      searchCriteria,
      [GC.FIELD_REPORT_NAME]: name,
      [GC.CURRENT_BRANCH]: branchGuid,
    };

    const params = { format: fileType };

    const options = { params, data: reqBody, resType: 'arraybuffer' };

    const endpoint = R.path([reportType, `${listType}Export`], templateEndpoints);

    const res = yield call(sendRequest, 'post', endpoint, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'info', 'messages:downloading-file');
    } else {
      yield call(G.handleFailResponse, res, 'exportReportDataRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'exportReportDataRequest exception');
  }
}
// report

// reference
function* createReferenceRequest({ payload }) {
  try {
    const { values, selectedList } = payload;

    yield put(openLoader());
    const reportType = yield select(makeSelectReportType());
    const endpoint = R.path([reportType, 'reference'], templateEndpoints);
    const options = {
      data: R.assoc('primaryObjectGuids', selectedList, values),
    };

    const res = yield call(sendRequest, 'post', endpoint, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.resetListAndPagination());
      yield put(A.getItemListRequest());
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
    } else {
      yield call(G.handleFailResponse, res, 'createReferenceRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'createReferenceRequest exception');
  }
}

// shared accessorials
function* restoreInheritedSharedAccessorialsRequest() {
  try {
    yield put(openLoader());
    const branchGuid = yield select(makeSelectCurrentBranchGuid());
    const options = {
      params: { [GC.BRANCH_GUID]: branchGuid },
    };

    const res = yield call(sendRequest, 'put', endpointsMap.restoreInheritedSharedAccessorial, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.resetListAndPagination());
      yield put(A.getItemListRequest());
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
    } else {
      yield call(G.handleFailResponse, res, 'restoreInheritedSharedAccessorialsRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.handleException, 'restoreInheritedSharedAccessorialsRequest exception');
  }
}

// route template
function* handleCreateRouteFromTemplateSaga({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const { singleRoute, editTemplate, temporaryTemplate, shouldGoToRouteBuilder } = payload;

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      data: R.assoc(GC.FIELD_BRANCH_GUID, branchGuid, R.omit(['shouldGoToRouteBuilder'], payload)),
    };

    const endpoint = G.ifElse(singleRoute, endpointsMap.createRouteFromTemplate, endpointsMap.createRoutesFromTemplate);

    const res = yield call(sendRequest, 'post', endpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'toasts:success:create');

      if (G.isTrue(shouldGoToRouteBuilder)) {
        yield put(setInitialRouteGuid(data));

        if (G.isTrue(editTemplate)) {
          yield put(setEditTemplateRoute(true));
        }

        yield delay(1000);

        yield call(G.goToRoute, routesMap.routeBuilder);
      } else if (temporaryTemplate) {
        yield call(G.goToRoute, GC.ROUTE_PATH_TEMPLATES_ROUTE_LIST);
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleCreateRouteFromTemplateSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleCreateRouteFromTemplateSaga  exception');
  }
}

function* updateRouteTemplateRequest({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const options = {
      data: payload,
    };

    const endpoint = endpointsMap.pureUpdateRouteTemplate;

    const res = yield call(sendRequest, 'put', endpoint, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'toasts:success:create');
    } else {
      yield call(G.handleFailResponse, res, 'updateRouteTemplateRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'updateRouteTemplateRequest  exception');
  }
}

// item
function* createOrUpdateTemplateItemRequest({ payload }) {
  try {
    yield put(openLoader());

    const {
      failCallback,
      successCallback,
      shouldGetItemList,
      shouldCloseModal = true,
      ...rest
    } = payload;

    const reportType = yield select(makeSelectReportType());
    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const isUpdate = G.isNotNilAndNotEmpty(G.getPropFromObject(GC.FIELD_VERSION, payload));
    const method = G.ifElse(isUpdate, 'put', 'post');
    const endpoint = R.path([reportType, 'template'], templateEndpoints);

    const options = {
      data: R.assoc(GC.BRANCH_GUID, branchGuid, rest),
    };

    const res = yield call(sendRequest, method, endpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      if (shouldCloseModal) yield put(closeModal());

      if (shouldGetItemList) {
        yield put(A.getItemListRequest({ guids: R.of(Array, G.getGuidFromObject(data)) }));
      } else {
        yield put(A.createOrUpdateTemplateItemSuccess(data));
      }

      G.callFunction(successCallback);

      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
    } else {
      G.callFunction(failCallback);

      yield call(G.handleFailResponse, res, 'createOrUpdateTemplateItemRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'createOrUpdateTemplateItemRequest exception');
  }
}

function* removeTemplateItemsRequest({ payload }) {
  try {
    yield put(openLoader());

    const options = { data: payload };

    const reportType = yield select(makeSelectReportType());

    const endpoint = R.path(
      [reportType, G.ifElse(R.equals(reportType, GC.COMPENSATION_TEMPLATE_REPORT), 'massDelete', 'template')],
      templateEndpoints,
    );

    const res = yield call(sendRequest, 'delete', endpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status, data)) {
      yield put(closeModal());

      let guids = G.ifElse(G.isArray(payload), payload, R.of(Array, G.getGuidFromObject(payload)));

      if (R.and(R.equals(status, 202), G.isNotNilAndNotEmpty(data))) {
        guids = R.difference(guids, R.keys(data));
      }

      if (G.isNotNilAndNotEmpty(guids)) {
        yield call(G.showToastrMessage, 'success', 'messages:success:remove');

        yield put(A.removeTemplateItemsSuccess(guids));
      }
    } else {
      yield call(G.handleFailResponse, res, 'removeTemplateItemsRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'removeTemplateItemsRequest exception');
  }
}

function* removeTemplateItemRequest({ payload }) {
  try {
    yield put(openLoader());

    const reportType = yield select(makeSelectReportType());

    const endpoint = R.path([reportType, 'remove'], templateEndpoints)(payload);

    const res = yield call(sendRequest, 'delete', endpoint);

    const { data, status } = res;

    if (G.isResponseSuccess(status, data)) {
      yield put(closeModal());
      yield put(A.removeTemplateItemsSuccess(R.of(Array, payload)));

      yield call(G.showToastrMessage, 'success', 'messages:success:remove');
    } else {
      yield call(G.handleFailResponse, res, 'removeTemplateItemRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'removeTemplateItemRequest exception');
  }
}

function* changeTemplateItemStatusRequest({ payload }) {
  try {
    const { guid, active, callback } = payload;

    yield put(openLoader({ showDimmer: true }));
    const reportType = yield select(makeSelectReportType());
    const endpoint = G.ifElse(
      G.isFalse(active),
      R.path([reportType, 'activate'], templateEndpoints),
      R.path([reportType, 'deactivate'], templateEndpoints),
    )(guid);

    const res = yield call(sendRequest, 'put', endpoint);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      if (G.isFunction(callback)) {
        callback();
      } else {
        yield put(closeModal());
        yield put(A.changeTemplateItemStatusSuccess(guid));
        yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
      }
    } else {
      yield call(G.handleFailResponse, res, 'changeTemplateItemStatusRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'changeTemplateItemStatusRequest exception');
  }
}

// compensation
function* createCompensationTemplateRequest({ payload }) {
  try {
    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      data: R.assoc(GC.BRANCH_GUID, branchGuid, payload),
    };

    const res = yield call(sendRequest, 'post', endpointsMap.compensationTemplate, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
    } else {
      yield call(G.handleFailResponse, res, 'createCompensationTemplateRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'createCompensationTemplateRequest exception');
  }
}

function* getCompensationTemplateListRequest({ payload }) {
  try {
    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: { [GC.BRANCH_GUID]: branchGuid },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.compensationTemplateList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const { callback } = payload;

      G.callFunctionWithArgs(callback, data);
    } else {
      yield call(G.handleFailResponse, res, 'getCompensationTemplateListRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'getCompensationTemplateListRequest exception');
  }
}

// configs
function* getBranchConfigsRequest({ payload }) {
  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: {
        names: R.join(', ', payload),
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.branchConfigsEndpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getBranchConfigsSuccess(G.mapConfigValuesByName(data)));
    } else {
      yield call(G.handleFailResponse, res, 'getBranchConfigsRequest fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'getBranchConfigsRequest exception');
  }
}

// service vendor
function* getServiceVendorListAvailableRequest() {
  try {
    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const types = `${GC.SERVICE_VENDOR_TYPE_FLEET_SERVICE},${GC.SERVICE_VENDOR_TYPE_FLEET_SELF_SERVICE}`;

    const options = {
      params: { types, [GC.BRANCH_GUID]: branchGuid },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.serviceVendorListAvailable, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getServiceVendorListAvailableSuccess(data));
    } else {
      yield call(G.handleFailResponse, res, 'getServiceVendorListAvailableRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield call(G.handleException, error, 'getServiceVendorListAvailableRequest exception');
  }
}

function* getConfigsByNamesSaga({ payload }) {
  try {
    const { names, branchGuid } = payload;

    const options = {
      params: { names, [GC.FIELD_BRANCH_GUID]: branchGuid },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.branchConfigsEndpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const mappedConfigs = G.mapConfigValuesByName(data);

      yield call(G.setAmousConfigToWindow, mappedConfigs);
    } else {
      yield call(G.handleFailResponse, res, 'getConfigsByNamesSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'getConfigsByNamesSaga exception');
  }
}

// visit page
function* visitTemplateListPage({ payload }) {
  while (true) { // eslint-disable-line
    const { reportType, checkVisitPage, listType = 'itemList' } = payload;

    yield call(visitPageSaga, payload, checkVisitPage);

    yield put(openLoader());

    const scope = getReferenceScopeByReportType(reportType);

    if (R.equals(reportType, GC.SHARED_ACCESSORIAL_REPORT)) {
      const branchGuid = yield select(makeSelectCurrentBranchGuid());

      yield call(getConfigsByNamesSaga, { payload: { branchGuid, names: GC.GENERAL_MODE_TRANSPORTATION } });
    }

    if (G.isNotNilAndNotEmpty(scope)) {
      yield put(getAllAvailableRefTypesByScopeRequest(scope));
    }

    yield put(A.setInitialState({ listType, reportType }));
    yield put(A.setReportPending());

    yield call(getAvailableReportsRequest, { payload: { setUsedReport: true } });

    yield put(A.getItemListRequest());

    yield put(closeLoader());

    break;
  }
}

function* templateReportWatcherSaga() {
  // visit pages
  yield takeLatest(GC.VISIT_ITEMS_LIST_PAGE, visitTemplateListPage);
  yield takeLatest(GC.VISIT_CONTACT_BOOK_PAGE, visitTemplateListPage);
  yield takeLatest(GC.VISIT_LOCATIONS_LIST_PAGE, visitTemplateListPage);
  yield takeLatest(GC.VISIT_DO_TEMPLATE_LIST_PAGE, visitTemplateListPage);
  yield takeLatest(GC.VISIT_SHARED_ACCESORIALS_PAGE, visitTemplateListPage);
  yield takeLatest(GC.VISIT_ROUTE_TEMPLATE_LIST_PAGE, visitTemplateListPage);
  yield takeLatest(GC.VISIT_CONTAINER_TEMPLATE_LIST_PAGE, visitTemplateListPage);
  yield takeLatest(GC.VISIT_COMPENSATION_TEMPLATE_LIST_PAGE, visitTemplateListPage);
  yield takeLatest(GC.VISIT_EQUIPMENT_SERVICE_TEMPLATE_LIST_PAGE, visitTemplateListPage);
  // report
  yield takeLatest(A.getItemListRequest, getItemListRequest);
  yield takeLatest(A.updateReportRequest, updateReportRequest);
  yield takeLatest(A.createReportRequest, createReportRequest);
  yield takeLatest(A.exportReportDataRequest, exportReportDataRequest);
  yield takeLatest(A.changeDefaultReportRequest, changeDefaultReportRequest);
  yield takeLatest(A.getAvailableReportsRequest, getAvailableReportsRequest);
  // route template
  yield takeLatest(A.updateRouteTemplateRequest, updateRouteTemplateRequest);
  yield takeLatest(A.createRouteFromTemplateRequest, handleCreateRouteFromTemplateSaga);
  // shared accessorials
  yield takeLatest(A.restoreInheritedSharedAccessorialsRequest, restoreInheritedSharedAccessorialsRequest);
  // item
  yield takeLatest(A.createReferenceRequest, createReferenceRequest);
  yield takeLatest(A.removeTemplateItemRequest, removeTemplateItemRequest);
  yield takeLatest(A.removeTemplateItemsRequest, removeTemplateItemsRequest);
  yield takeLatest(A.changeTemplateItemStatusRequest, changeTemplateItemStatusRequest);
  yield takeLatest(A.createOrUpdateTemplateItemRequest, createOrUpdateTemplateItemRequest);
  // compensation
  yield takeLatest(A.createCompensationTemplateRequest, createCompensationTemplateRequest);
  yield takeLatest(A.getCompensationTemplateListRequest, getCompensationTemplateListRequest);
  // configs
  yield takeLatest(A.getBranchConfigsRequest, getBranchConfigsRequest);
  // service vendor
  yield takeLatest(A.getServiceVendorListAvailableRequest, getServiceVendorListAvailableRequest);
}

export default templateReportWatcherSaga;

export {
  getItemListRequest,
  getAvailableReportsRequest,
  changeDefaultReportRequest,
  removeTemplateItemsRequest,
  changeTemplateItemStatusRequest,
  handleCreateRouteFromTemplateSaga,
  createOrUpdateTemplateItemRequest,
};
